import {Box, Chip, Stack, TextField, Typography} from '@mui/material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {styled} from '@mui/material/styles';
import {Reservation} from 'src/apis/autoGenerate/types/reservation';
import {reservationServiceFind} from 'src/apis/autoGenerate/services';
import message from 'src/components/Message';
import {dayjs, fmtInTz, tzLabel, DEFAULT_TIMEZONE} from 'src/utils/time';
import {useOrgId} from 'src/hooks';

const StyledDataGrid = styled(DataGrid)(({theme}) => ({
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-sortIcon': {
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon, & .MuiDataGrid-columnHeaderActive .MuiDataGrid-sortIcon':
    {
      opacity: 0.7,
    },
}));

const STATUS_COLORS: Record<string, 'success' | 'default' | 'error' | 'warning'> = {
  active: 'success',
  cancelled: 'default',
  expired: 'warning',
};

export type AmenityReservationsPanelProps = {
  /** When set, only reservations for this amenity are loaded */
  amenityId?: string;
  /** IANA timezone of the parent apartment. Falls back to default US/Eastern. */
  apartmentTimeZone?: string;
};

export default function AmenityReservationsPanel({
  amenityId,
  apartmentTimeZone,
}: AmenityReservationsPanelProps) {
  const tz = apartmentTimeZone || DEFAULT_TIMEZONE;
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  // Default the date filter to "today in the apartment's tz", not browser-local.
  const [dateFilter, setDateFilter] = useState(() => dayjs().tz(tz).format('YYYY-MM-DD'));
  const {orgId} = useOrgId();

  // If the apartment tz changes (user switches apartment), reset the date filter
  // so we don't keep a stale "today" from the old timezone.
  useEffect(() => {
    setDateFilter(dayjs().tz(tz).format('YYYY-MM-DD'));
  }, [tz]);

  const fetchReservations = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await reservationServiceFind(dateFilter || undefined, amenityId);
      const data = res?.data as {reservations?: Reservation[]};
      setReservations(data?.reservations || []);
    } catch {
      message.error('Failed to load reservations');
    } finally {
      setLoading(false);
    }
  }, [orgId, dateFilter, amenityId]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const columns: GridColDef[] = useMemo(
    () => [
      {field: 'amenityName', headerName: 'Amenity', flex: 1, minWidth: 150},
      {field: 'userName', headerName: 'User', width: 160},
      {field: 'userEmail', headerName: 'Email', width: 200},
      {
        field: 'startTime',
        headerName: 'Start',
        width: 180,
        renderCell: params => fmtInTz(params.value, tz),
      },
      {
        field: 'endTime',
        headerName: 'End',
        width: 180,
        renderCell: params => fmtInTz(params.value, tz),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: params => (
          <Chip
            label={params.value}
            size="small"
            color={STATUS_COLORS[params.value as string] || 'default'}
          />
        ),
      },
    ],
    [tz]
  );

  const tzShortLabel = tzLabel(tz);

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1}}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 1}}>
        <TextField
          type="date"
          label="Date"
          size="small"
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          InputLabelProps={{shrink: true}}
        />
        <Typography variant="caption" color="text.secondary">
          All times shown in {tzShortLabel ? `${tzShortLabel} (${tz})` : tz}
        </Typography>
      </Stack>

      <Box sx={{flex: 1, minHeight: 360}}>
        <StyledDataGrid
          rows={reservations}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{pagination: {paginationModel: {pageSize: 25}}}}
          getRowId={row => row.id || ''}
        />
      </Box>
    </Box>
  );
}
